import { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { errorMessage, formatDateTime } from "helpers/common";
import { api } from "helpers/axios";

import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { en, de } from 'yup-locales';
import { useForm } from "react-hook-form";
import ErrorAlert from "components/ui/ErrorAlert";
import { Button, Field, Input, Modal } from "components/ui";
import { useTranslation } from "translation/useTranslation";
import { getLang } from "store/userSlice";

export default function ImportSources() {
  const lang = useSelector(getLang);

  const { translate, editableTranslate } = useTranslation();

  const [showSourceModal, setShowSourceModal] = useState(false);
  const [loading, setLoading] = useState(false);
  // Which row is being deleted — a shared boolean would spin every row's button.
  const [deletingId, setDeletingId] = useState(null);
  const [error, setError] = useState('');
  const [newSourceError, setNewSourceError] = useState('');
  const [sources, setSources] = useState([]);
  const [sourceType, setSourceType] = useState('sftp');

  const schema = yup.object({
    source_name: yup.string().required(translate('employee_import_source.source_name_required', 'Quellname ist erforderlich')),
    source_description: yup.string().notRequired(),
    source_type: yup.string().required(translate('employee_import_source.source_type_required', 'Quelltyp ist erforderlich')),
    source_url: yup.string().when('source_type', {
      is: (value) => value === 'sftp',
      // required and must be a valid URL with sftp:// or sftp:// prefix
      then: () => yup.string().required(translate('employee_import_source.sftp_url_required', 'SFTP-URL ist erforderlich')).matches(/^sftp:\/\//, translate('employee_import_source.sftp_url_invalid', 'Die SFTP-URL muss mit sftp:// beginnen')),
      otherwise: () => yup.string().notRequired()
    }),
    source_delimeter: yup.string().when('source_type', {
      is: (value) => value === 'sftp',
      then: () => yup.string().required(translate('employee_import_source.delimeter_required', 'Trennzeichen ist erforderlich')),
      otherwise: () => yup.string().notRequired()
    }),
    source_username: yup.string().when('source_type', {
      is: (value) => value === 'sftp' || value === 'active_directory',
      then: () => yup.string().required(translate('employee_import_source.username_required', 'Benutzername ist erforderlich')),
      otherwise: () => yup.string().notRequired()
    }),
    source_password: yup.string().when('source_type', {
      is: (value) => value === 'sftp' || value === 'active_directory',
      then: () => yup.string().required(translate('employee_import_source.password_required', 'Passwort ist erforderlich')),
      otherwise: () => yup.string().notRequired()
    }),
    domain: yup.string().when('source_type', {
      is: (value) => value === 'active_directory',
      then: () => yup.string().required(translate('employee_import_source.domain_required', 'Domäne ist erforderlich')),
      otherwise: () => yup.string().notRequired()
    })
  });

  const { register, reset, handleSubmit, watch, formState: { errors } } = useForm({
    resolver: yupResolver(schema)
  });

  const watchSourceType = watch('source_type');

  useEffect(() => {
    yup.setLocale(lang === 'en' ? en : de);
  }, [lang]);

  useEffect(() => {
    fetchSources();
  }, []);

  // if sftp,  sftp url, username, password are required
  // if active_directory, domain, username, password are required
  useEffect(() => {
    if (!watchSourceType) return;

    setSourceType(watchSourceType);

  }, [watchSourceType]);

  const fetchSources = async () => {
    try {
      setLoading(true);
      const { data, status } = await api.get('/employee-import-sources');

      if (status === 200) {
        setSources(data.data);
      }
    } catch (err) {
      setError(errorMessage(err, 'fetchSources'));
    }

    setLoading(false);
  }

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      setError('');

      let payload = {
        source_name: data.source_name,
        source_description: data.source_description,
        source_type: data.source_type,
        source_username: data.source_username,
        source_password: data.source_password,
      };

      if (data.source_type === 'sftp') {
        payload.source_url = data.source_url;
        payload.source_delimeter = data.source_delimeter;
      } else if (data.source_type === 'active_directory') {
        payload.domain = data.domain;
      }

      const { status } = await api.post('/employee-import-sources', payload);

      if (status === 201) {
        reset();
        fetchSources();
        setShowSourceModal(false);
        setNewSourceError('');
        setSourceType('sftp');
      }
    } catch (err) {
      setNewSourceError(errorMessage(err, 'onSubmit'));
    }

    setLoading(false);
  }

  const handleDelete = async (id) => {
    try {
      setDeletingId(id);
      setError('');

      const { status } = await api.delete(`/employee-import-sources/${id}`);

      if (status === 200) {
        fetchSources();
      }
    } catch (err) {
      setError(errorMessage(err, 'handleDelete'));
    }

    setDeletingId(null);
  }

  return (
    <>
      <div className="col-lg col-md-9 section-col-right d-flex">
        <div className={`bg-white w-100 p-4 content-card`}>
          <div>
            <h4>{editableTranslate('global.menu.settings.employee_import_sources', 'Datenimport Beschäftigte')}</h4>
            <div className="text-right">
              <Button variant="secondary" onClick={() => setShowSourceModal(true)} data-testid="create-source-button">
                <i className="bi bi-plus"></i> {editableTranslate('employee_import_source.new_source', 'Datei')}
              </Button>
            </div>
          </div>
          {
            error && <div className="mt-4 mb-3">
              <ErrorAlert error={error} onClose={() => setError(null)} isHtml={true} />
            </div>
          }
          <div className="mt-2">
            <div className="mt-3">
              <div className="table-responsive">
                <table className="table common-table" data-testid="import-sources-table">
                  <thead>
                    <tr>
                      <th scope="col" className="">#</th>
                      <th scope="col" className="">{editableTranslate('employee_import_source.name', 'Name')}</th>
                      <th scope="col" className="">{editableTranslate('employee_import_source.source_description', 'Quellbeschreibung')}</th>
                      <th scope="col" className="">{editableTranslate('employee_import_source.type', 'Typ')}</th>
                      <th scope="col" className="">{editableTranslate('employee_import_source.is_active', 'Ist aktiv')}</th>
                      <th scope="col" className="">{editableTranslate('employee_import_source.last_checked', 'Zuletzt geprüft')}</th>
                      <th scope="col" className="">{editableTranslate('employee_import_source.config', 'Konfiguration')}</th>
                      <th scope="col" className="">{editableTranslate('global.actions', 'Aktion')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {
                      !loading && Array.isArray(sources) && sources.length > 0 && sources.map((item, index) => (
                        <tr key={index} data-testid={`import-source-row-${item.hash_id}`}>
                          <td>{index + 1}</td>
                          <td>{item.name}</td>
                          <td>{item.description || '-'}</td>
                          <td>
                            {
                              item.source_type === 'sftp' ? (
                                <span><i className="bi bi-server"></i> SFTP</span>
                              ) : (
                                <span><i className="bi bi-windows"></i> Active Directory</span>
                              )
                            }
                          </td>
                          <td><i className={`bi bi-check-circle-fill ${item.is_active ? 'text-success' : 'text-danger'}`} aria-hidden="true"></i></td>
                          <td>
                            {item.last_checked_at ? formatDateTime(item.last_checked_at) : editableTranslate('employee_import_source.never_checked', 'Nie geprüft')}
                          </td>
                          <td>
                            <div id={`config-${item.id}`}>
                              <code><pre>{JSON.stringify(item.config, null, 2)}</pre></code>
                            </div>
                          </td>
                          <td>
                            <Button onClick={() => handleDelete(item.hash_id)} variant="primary" className="ml-2" loading={deletingId === item.hash_id} disabled={!!deletingId} data-testid="delete-button">
                              <span className="bi bi-trash"></span> {editableTranslate('global.delete')}
                            </Button>
                          </td>
                        </tr>
                      ))
                    }
                    {
                      (loading || !sources) && <tr>
                        <td colSpan={6}>
                          <div className="d-flex justify-content-center" data-testid="loading">
                            <div className="spinner-border" role="status">
                              <span className="sr-only">Wird geladen...</span>
                            </div>
                          </div>
                        </td>
                      </tr>
                    }
                    {
                      !loading && Array.isArray(sources) && sources.length === 0 && <tr>
                        <td colSpan={6} className="text-center" data-testid="empty-state">{editableTranslate('global.no_data')}</td>
                      </tr>
                    }
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal
        open={showSourceModal}
        title={editableTranslate('employee_import_source.source', 'Dateiquelle')}
        onClose={() => setShowSourceModal(false)}
        loading={loading}
        testIdPrefix="source-"
        footer={<>
          <Button variant="secondary" onClick={() => { reset(); setShowSourceModal(false); }} data-testid="cancel-button">
            {editableTranslate('global.cancel', 'Abbrechen')}
          </Button>
          <Button variant="primary" type="submit" form="source-form" loading={loading} data-testid="save-button">
            {editableTranslate('global.save', 'Speichern')}
          </Button>
        </>}
      >
        <div className="text-center">
          <div className="btn-group btn-group-toggle w-50" data-toggle="buttons">
            <label className={`btn ${sourceType === 'sftp' ? 'btn-primary' : 'btn-secondary'}`}>
              <input type="radio" name="source_type" className="no-w-h" value="sftp" {...register('source_type')} defaultChecked data-testid="source-type-sftp-radio" />
              <i className="bi bi-server"></i><br />
              SFTP
            </label>
            {/* <label className={`btn ${sourceType === 'active_directory' ? 'btn-primary' : 'btn-secondary'}`}>
              <input type="radio" name="source_type" value='active_directory' {...register('source_type')} data-testid="source-type-active-directory-radio" />
                  <i className="bi bi-windows"></i><br/>
                  Active Directory
            </label> */}
          </div>
        </div>
        {
          newSourceError && <div className="mt-3">
            <ErrorAlert error={newSourceError} onClose={() => setNewSourceError(null)} isHtml={true} />
          </div>
        }
        {/* if sftp, ask for sftp url, username, password */}
        <form id="source-form" className="mt-3" onSubmit={handleSubmit(onSubmit)} data-testid="source-form">
          <Field label={editableTranslate('employee_import_source.source_name', 'Name der Quelle')} htmlFor="source_name" error={errors.source_name?.message} className="peb-field-mb">
            <Input type="text" id="source_name" error={!!errors.source_name} {...register('source_name')} data-testid="source-name-input" />
          </Field>
          {
            sourceType === 'active_directory' ? (
              <Field label={editableTranslate('employee_import_source.domain', 'Domäne')} htmlFor="domain" error={errors.domain?.message} className="peb-field-mb">
                <Input type="text" id="domain" error={!!errors.domain} {...register('domain')} data-testid="domain-input" />
              </Field>
            ) : <>
              <Field label={editableTranslate('employee_import_source.sftp_url', 'SFTP-URL')} htmlFor="sftp_url" error={errors.source_url?.message} className="peb-field-mb">
                <Input type="text" id="sftp_url" error={!!errors.source_url} {...register('source_url')} data-testid="sftp-url-input" />
              </Field>
              <Field label={editableTranslate('employee_import_source.delimeter', 'Trennzeichen')} htmlFor="source_delimeter" error={errors.source_delimeter?.message} className="peb-field-mb">
                <Input type="text" id="source_delimeter" error={!!errors.source_delimeter} {...register('source_delimeter')} data-testid="source-delimeter-input" />
              </Field>
            </>
          }
          <Field label={editableTranslate('employee_import_source.username', 'Nutzende')} htmlFor="source_username" error={errors.source_username?.message} className="peb-field-mb">
            <Input type="text" id="source_username" error={!!errors.source_username} {...register('source_username')} data-testid="source-username-input" />
          </Field>
          <Field label={editableTranslate('employee_import_source.password', 'Passwort')} htmlFor="source_password" error={errors.source_password?.message} className="peb-field-mb">
            <Input type="password" id="source_password" error={!!errors.source_password} {...register('source_password')} data-testid="source-password-input" />
          </Field>
          <Field label={editableTranslate('employee_import_source.source_description', 'Quellbeschreibung')} htmlFor="source_description" error={errors.source_description?.message} className="peb-field-mb">
            <textarea className={`peb-input peb-input--textarea ${errors.source_description ? 'peb-input--error' : ''}`} id="source_description" {...register('source_description')} rows="3" data-testid="source-description-input"></textarea>
          </Field>
        </form>
      </Modal>
    </>
  );
}
